import { z } from 'zod';
import { AttendanceProfileSchema, WorkforceTypeSchema } from './employee.schema.js';
import { MovementMethodSchema } from './companyMovements.schema.js';
import { WorkforceGroupQuerySchema } from '../filters/workforceGroup.js';

/**
 * Live movement feed — the dashboard's "Latest movements" panel.
 *
 * One row per attendance event, newest first, with the scan quality that
 * produced it. Camp occupancy is a slow-moving number already carried by the
 * map pins and the exec tile; what is genuinely live at a camp is the stream of
 * departures and arrivals, so this feed replaces the camp totals table.
 *
 * Scan quality comes from the existing 1:1 `FieldEventMeta` side table — NOT
 * from `FieldScanTelemetry`, which is deliberately anonymous ("metrics ONLY —
 * NO images, NO worker code, NO PII") and must stay that way.
 *
 * See docs/movement-feed-endpoint-spec-2026-07-21.md.
 */

/**
 * A journey leg, derived from `worksite.type` × punch direction rather than
 * from a dedicated event type — matching the camp↔site journey decision that
 * a leg is `worksite.type` × IN/OUT with no new EventType values.
 *
 * `UNKNOWN` covers an event whose worksite is null (worksiteId is nullable on
 * AttendanceEvent). Such an event MUST still surface: a punch with no location
 * is more worth seeing, not less.
 */
export const MovementLegSchema = z.enum([
  'CAMP_OUT',
  'CAMP_IN',
  'SITE_IN',
  'SITE_OUT',
  'UNKNOWN',
]);
export type MovementLeg = z.infer<typeof MovementLegSchema>;

/** Filter chips on the panel. `flagged` spans any non-VALID status. */
export const MovementFeedKindSchema = z.enum(['all', 'out', 'in', 'flagged']);
export type MovementFeedKind = z.infer<typeof MovementFeedKindSchema>;

/**
 * Per-event scan quality. Null for any event that did not come from a FIELD
 * device (a WEB/KIOSK punch has no FieldEventMeta row).
 */
export const MovementScanSchema = z.object({
  /** FACE = 1:N face match; MANUAL_ID = operator-attested selection. */
  method: z.enum(['FACE', 'MANUAL_ID']),
  /** Top-1 cosine similarity of the face match, when there was one. */
  matchScore: z.number().nullable(),
  livenessScore: z.number().nullable(),
  /**
   * True when an operator manually selected the worker AFTER a face scan had
   * already failed N attempts (FieldEventMeta.failedScanEventId is set). The
   * sharpest buddy-punch signal available — a plain manual entry is routine,
   * a manual entry that follows a failed scan is not.
   */
  afterFailedScan: z.boolean(),
});
export type MovementScan = z.infer<typeof MovementScanSchema>;

export const MovementFeedItemSchema = z.object({
  id: z.string(),
  /** Server-authoritative event time (ISO). */
  at: z.string(),
  leg: MovementLegSchema,
  /**
   * The punch's own direction. `leg` folds a punch with no worksite into
   * UNKNOWN, which loses whether it was an arrival — and the wall ticker then
   * said "left" for a browser check-in (owner, 2026-10-24: "why left?").
   * Null for a break. Optional so an older server's rows still parse.
   */
  direction: z.enum(['IN', 'OUT']).nullable().optional(),
  employee: z.object({
    id: z.string(),
    name: z.string(),
    code: z.string().nullable(),
    /**
     * WHICH POPULATION THE PERSON BELONGS TO — `Employee.workforceType`, the
     * same first-class column the pillar switch, the roster group-filter and
     * the report scope key off. Carried for a reader of the row, and it is
     * deliberately not re-inferred from `employeeCode`'s prefix or from
     * `userId == null`: those were the inference this column replaced, and a
     * second spelling of the split is how two screens come to disagree about
     * who is staff.
     *
     * THE PANEL NO LONGER GROUPS ON IT, and this note used to say it did. The
     * column is `@default(OFFICE_STAFF)` in the schema, so every worker any
     * import has produced carries that value without anybody choosing it — a
     * batch rule keyed off it folded nothing at all on a real estate while
     * passing every test that set the field. The fold now reads the PUNCH's own
     * source (`scan != null`, i.e. it came off a field device); see
     * `apps/web/src/routes/dashboard/movementGrouping.ts`.
     *
     * Nullable, because `AttendanceEvent.employeeId` is: an event with no
     * employee row still surfaces, and "we do not know which population this
     * is" is not `SITE_WORKER` (3.3).
     */
    workforceType: WorkforceTypeSchema.nullable(),
  }),
  worksite: z
    .object({
      id: z.string(),
      name: z.string(),
      type: z.string(),
    })
    .nullable(),
  source: z.string(),
  status: z.string(),
  /**
   * Human-readable reason an event was flagged — the punch path already writes
   * near-margin and low-liveness text here (e.g. "Near-margin match 0.61
   * (floor 0.55 +0.05)"), so the feed surfaces it verbatim rather than
   * recomputing a signal the server already decided.
   */
  flaggedReason: z.string().nullable(),
  scan: MovementScanSchema.nullable(),
});
export type MovementFeedItem = z.infer<typeof MovementFeedItemSchema>;

export const MovementFeedResponseSchema = z.object({
  movements: z.array(MovementFeedItemSchema),
  /**
   * Opaque `(serverTimestamp, id)` cursor. It is NOT a bare timestamp: bulk
   * gate roll-calls ingest many events sharing one timestamp, and a
   * timestamp-only cursor silently drops every row after the first.
   */
  nextCursor: z.string().nullable(),
  counts: z.object({
    /** Total movements in the queried window, within the caller's scope. */
    returned: z.number().int(),
    /**
     * Flagged count over the SAME scope and window as the rows — a chip that
     * counts globally while the list is site-filtered is a lie.
     */
    flagged: z.number().int(),
  }),
});
export type MovementFeedResponse = z.infer<typeof MovementFeedResponseSchema>;

const csvOrArray = (v: unknown): unknown =>
  typeof v === 'string' ? v.split(',').map((s) => s.trim()).filter((s) => s !== '') : v;

export const MovementFeedQuerySchema = z.object({
  after: z.string().optional(),
  limit: z.coerce.number().int().min(1).max(100).optional(),
  kind: MovementFeedKindSchema.optional(),
  worksiteId: z.string().optional(),
  /** Look-back window in hours (default 12, max 72). */
  hours: z.coerce.number().int().min(1).max(72).optional(),
  /*
   * THE BOARD'S FILTERS, SO THE MAP'S FEED FOLLOWS THE MAP (owner, 2026-10-29:
   * "if we toggle remote … show only for remote"). Every one is ANDed under the
   * caller's scope, never in place of it, and narrows the flagged count too.
   */
  /** Any of these sites — the Sites board's search and filters, resolved to the ids it shows. "a,b" or an array. */
  worksiteIds: z.preprocess(csvOrArray, z.array(z.string().min(1)).max(500)).optional(),
  /** The workforce group — Workers · Staff · Remote, the app's one rule (filters/workforceGroup.ts). */
  group: WorkforceGroupQuerySchema.optional(),
  /** The older spellings of `group`, read as the group they spelled (`workforceGroupFromParams`). */
  workforceType: WorkforceTypeSchema.optional(),
  attendanceProfile: AttendanceProfileSchema.optional(),
  departmentId: z.string().min(1).optional(),
  /** A position by its id (owner D2) — the registry's `position`, sent as `designationId`. */
  designationId: z.string().min(1).max(64).optional(),
  /** @deprecated A position by its TITLE — two designations sharing one merged. Read for an older client. */
  position: z.string().trim().min(1).max(120).optional(),
  /** Every word in the name, the code or the department — the People list's rule. */
  search: z.string().trim().min(1).max(120).optional(),
  /** How the punch was recorded. */
  method: MovementMethodSchema.optional(),
  /** Only punches a person has to look at: FLAGGED or PENDING_APPROVAL (`needsReview`). */
  review: z.preprocess((v) => (v === 'true' ? true : v === 'false' ? false : v), z.boolean()).optional(),
});
export type MovementFeedQuery = z.infer<typeof MovementFeedQuerySchema>;
